import React, { useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, NoticeBox } from "../../ui";
import BranchPicker from "./BranchPicker";
import { createChangeRequestAPI, editChangeRequestAPI, getApplicationDetailAPI } from "../../../utils/api/applications";
import WizardSteps from "./WizardSteps";
import { errorMessage } from "./applicationsText";

// Activation: always per branch. Shows every store branch with its integration
// status, lets the user pick eligible ones, confirms, and sends an activation
// request ("in progress"). Active branches stay as they are; only approved
// branches become active.
export default function ActivateBranchesModal({ open, storeId, app, appName, t, token, request = null, initialBranchIds = [], onClose, onDone, onEditInfo }) {
  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !app) return undefined;
    let current = true;
    setLoading(true);
    setStep(0);
    setError("");
    setSelected(request ? request.branchIds : []);
    getApplicationDetailAPI(token, storeId, app.key)
      .then((res) => {
        if (!current) return;
        setDetail(res.data.integration);
        // The branch the flow was opened from starts selected (if it can be activated).
        if (!request) setSelected(res.data.integration.branches.filter((branch) => initialBranchIds.includes(branch.id) && branch.activationEligible).map((branch) => branch.id));
      })
      .catch((e) => current && setError(errorMessage(t, e)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [open, app, storeId, token, t, request, initialBranchIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !app) return null;

  const branches = detail?.branches || [];
  const own = (branch) => Boolean(request) && branch.pendingRequest?.id === request.id;
  const eligible = (branch) => branch.activationEligible || own(branch);
  const reasonFor = (branch) => {
    if (eligible(branch)) return "";
    if (branch.pendingRequest) return t.flow.hasPending;
    if (branch.integration === "active") return t.status.active;
    if (branch.integration === "unconfigured" || !branch.appId) return t.flow.needsInfo;
    return t.flow.ineligible;
  };
  const anyEligible = branches.some(eligible);
  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  const chosen = branches.filter((branch) => selected.includes(branch.id));

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      if (request) await editChangeRequestAPI(token, storeId, request.id, { version: request.version, branchIds: selected });
      else await createChangeRequestAPI(token, storeId, app.key, { type: "activation", branchIds: selected });
      onDone?.(request ? t.flow.requestUpdated : t.flow.requestSent, "success");
    } catch (e) {
      if (alive.current) setError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const footer = (
    <>
      <Button variant="neutral" appearance="soft" onClick={step === 0 ? onClose : () => setStep(0)} disabled={busy}>
        {step === 0 ? t.cancel : t.back}
      </Button>
      {step === 0 ? (
        <Button variant="primary" appearance="filled" onClick={() => setStep(1)} disabled={!selected.length || loading}>
          {t.next}
        </Button>
      ) : (
        <Button variant="success" appearance="filled" onClick={submit} disabled={busy} loading={busy}>
          {request ? t.flow.saveChanges : t.flow.activateSubmit}
        </Button>
      )}
    </>
  );

  return (
    <LudDashboardModal open title={t.flow.activateTitle(appName)} size="lg" onClose={onClose} busy={busy} footer={footer}>
      <WizardSteps steps={t.flow.activateSteps} index={step} />
      {error && <NoticeBox variant="danger" size="sm">{error}</NoticeBox>}
      {loading && <p className="lud-app-lead" role="status">{t.loading}</p>}

      {!loading && detail && step === 0 && (
        <div className="lud-app-stack">
          <p className="lud-app-lead">{t.flow.activatePick}</p>
          {!anyEligible && (
            <NoticeBox variant="warning" size="sm" title={t.flow.activateNone}>
              {t.flow.activateGuide}{" "}
              <Button size="sm" variant="primary" appearance="soft" onClick={() => onEditInfo?.(app)}>{t.flow.editInfo}</Button>
            </NoticeBox>
          )}
          <BranchPicker branches={branches} selected={selected} onToggle={toggle} reasonFor={reasonFor} originId={initialBranchIds[0] || null} t={t} disabled={busy} />
        </div>
      )}

      {!loading && detail && step === 1 && (
        <div className="lud-app-stack">
          <NoticeBox variant="info" size="sm">{t.flow.activateConfirm}</NoticeBox>
          <strong>{t.flow.selectedBranches}</strong>
          <ul className="lud-app-branch-list">
            {chosen.map((branch) => (
              <li key={branch.id}>
                <strong>{branch.name}</strong>
                <small>{`${t.status[branch.integration]} → ${t.status.active}`}</small>
                <small dir="ltr">{[branch.appId, branch.appName].filter(Boolean).join(" · ")}</small>
              </li>
            ))}
          </ul>
          <small className="lud-app-hint">{t.flow.unaffected}</small>
        </div>
      )}
    </LudDashboardModal>
  );
}
